import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Alert,
  Grid,
  MenuItem,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Close as CancelIcon,
  SwapHoriz as SwapIcon,
  Schedule as ExtendIcon,
  Gavel as ForceIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { usePermissions } from "../../../hooks/usePermissions";
import lectureSwapService from "../../../services/lectureSwapService";
import {
  LECTURE_SWAP_RESPOND_PERMISSION,
  LECTURE_SWAP_FORCE_PERMISSION,
  LECTURE_EXTEND_RESPOND_PERMISSION,
  LECTURE_EXTEND_FORCE_PERMISSION,
} from "../../../utils/lectureSwapPermissions";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../../config/pagination";
import { DATE_DISPLAY_FORMAT, displayDate } from "../../../utils/dateUtils";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
];

const SWAP_MODE_OPTIONS = [
  { value: "", label: "All Modes" },
  { value: "same_day", label: "Same Day" },
  { value: "cross_day", label: "Cross Day" },
];

const getStatusColor = (status) => {
  switch (status) {
    case "pending":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "error";
    case "cancelled":
      return "default";
    case "expired":
      return "info";
    default:
      return "default";
  }
};

function LectureSwapManagement() {
  const { hasVisiblePermission } = usePermissions();

  // Permission checks
  const canRespondSwap = hasVisiblePermission(LECTURE_SWAP_RESPOND_PERMISSION);
  const canForceSwap = hasVisiblePermission(LECTURE_SWAP_FORCE_PERMISSION);
  const canRespondExtend = hasVisiblePermission(LECTURE_EXTEND_RESPOND_PERMISSION);
  const canForceExtend = hasVisiblePermission(LECTURE_EXTEND_FORCE_PERMISSION);

  // Tab state: 0 = swap requests, 1 = extend requests
  const [activeTab, setActiveTab] = useState(0);

  // Swap state
  const [swapRequests, setSwapRequests] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapError, setSwapError] = useState("");
  const [swapTotal, setSwapTotal] = useState(0);
  const [swapPage, setSwapPage] = useState(0);
  const [swapPageSize, setSwapPageSize] = useState(20);

  // Extend state
  const [extendRequests, setExtendRequests] = useState([]);
  const [extendLoading, setExtendLoading] = useState(false);
  const [extendError, setExtendError] = useState("");
  const [extendTotal, setExtendTotal] = useState(0);
  const [extendPage, setExtendPage] = useState(0);
  const [extendPageSize, setExtendPageSize] = useState(20);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [swapModeFilter, setSwapModeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, request: null });

  // Fetch swap requests
  const fetchSwapRequests = useCallback(async () => {
    setSwapLoading(true);
    setSwapError("");
    try {
      const params = {
        page: swapPage + 1,
        limit: swapPageSize,
      };
      if (statusFilter) params.status = statusFilter;
      if (swapModeFilter) params.swapMode = swapModeFilter;
      if (dateFrom) params.dateFrom = dayjs(dateFrom).format("YYYY-MM-DD");
      if (dateTo) params.dateTo = dayjs(dateTo).format("YYYY-MM-DD");

      const response = await lectureSwapService.listSwapRequests(params);
      const data = response.data?.data || response.data;
      setSwapRequests(data?.requests || data?.rows || []);
      setSwapTotal(data?.total || data?.count || 0);
    } catch (err) {
      setSwapError(err?.response?.data?.message || "Failed to load swap requests");
    } finally {
      setSwapLoading(false);
    }
  }, [swapPage, swapPageSize, statusFilter, swapModeFilter, dateFrom, dateTo]);

  // Fetch extend requests
  const fetchExtendRequests = useCallback(async () => {
    setExtendLoading(true);
    setExtendError("");
    try {
      const params = {
        page: extendPage + 1,
        limit: extendPageSize,
      };
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.dateFrom = dayjs(dateFrom).format("YYYY-MM-DD");
      if (dateTo) params.dateTo = dayjs(dateTo).format("YYYY-MM-DD");

      const response = await lectureSwapService.listExtendRequests(params);
      const data = response.data?.data || response.data;
      setExtendRequests(data?.requests || data?.rows || []);
      setExtendTotal(data?.total || data?.count || 0);
    } catch (err) {
      setExtendError(err?.response?.data?.message || "Failed to load extend requests");
    } finally {
      setExtendLoading(false);
    }
  }, [extendPage, extendPageSize, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchSwapRequests();
    } else {
      fetchExtendRequests();
    }
  }, [activeTab, fetchSwapRequests, fetchExtendRequests]);

  // Actions
  const handleAction = (action, request) => {
    setConfirmDialog({ open: true, action, request });
  };

  const executeAction = async () => {
    const { action, request } = confirmDialog;
    if (!action || !request) return;

    setActionLoading(true);
    setConfirmDialog({ open: false, action: null, request: null });

    try {
      const id = request.uuid || request.id;

      if (activeTab === 0) {
        // Swap actions
        switch (action) {
          case "accept":
            await lectureSwapService.acceptSwapRequest(id, {
              receivingSubjectId: request.receivingSubjectId,
            });
            break;
          case "reject":
            await lectureSwapService.rejectSwapRequest(id);
            break;
          case "cancel":
            await lectureSwapService.cancelSwapRequest(id);
            break;
          default:
            break;
        }
        setSuccessMsg(`Swap request ${action}ed successfully`);
        fetchSwapRequests();
      } else {
        // Extend actions
        switch (action) {
          case "accept":
            await lectureSwapService.acceptExtendRequest(id);
            break;
          case "reject":
            await lectureSwapService.rejectExtendRequest(id);
            break;
          case "cancel":
            await lectureSwapService.cancelExtendRequest(id);
            break;
          default:
            break;
        }
        setSuccessMsg(`Extend request ${action}ed successfully`);
        fetchExtendRequests();
      }
    } catch (err) {
      setSwapError(err?.response?.data?.message || `Failed to ${confirmDialog.action} request`);
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMsg(""), 4000);
    }
  };

  const handleForceSwap = () => {
    // Force swap — placeholder for future modal with slot selection
    setSuccessMsg("Force swap feature — use the force swap form to specify slots");
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const handleForceExtend = () => {
    // Force extend — placeholder for future modal with slot selection
    setSuccessMsg("Force extend feature — use the force extend form to specify slots");
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  // Swap request columns
  const swapColumns = [
    {
      field: "swapMode",
      headerName: "Mode",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value === "same_day" ? "Same Day" : "Cross Day"}
          size="small"
          variant="outlined"
          color={params.value === "same_day" ? "primary" : "secondary"}
        />
      ),
    },
    {
      field: "initiatingTeacher",
      headerName: "Initiator",
      width: 150,
      valueGetter: (params) =>
        params.row.initiatingTeacher?.name || params.row.initiatingTeacherName || "—",
    },
    {
      field: "receivingTeacher",
      headerName: "Receiver",
      width: 150,
      valueGetter: (params) =>
        params.row.receivingTeacher?.name || params.row.receivingTeacherName || "—",
    },
    {
      field: "sourceSlot",
      headerName: "Source Slot",
      width: 180,
      valueGetter: (params) => {
        const slot = params.row.sourceSlot || {};
        return `${slot.dayOfWeek || ""} ${slot.startTime || ""}-${slot.endTime || ""}`;
      },
    },
    {
      field: "targetSlot",
      headerName: "Target Slot",
      width: 180,
      valueGetter: (params) => {
        const slot = params.row.targetSlot || {};
        return `${slot.dayOfWeek || ""} ${slot.startTime || ""}-${slot.endTime || ""}`;
      },
    },
    {
      field: "sourceSwapDate",
      headerName: "Source Date",
      width: 120,
      valueFormatter: (params) => params.value ? displayDate(params.value) : "—",
    },
    {
      field: "targetSwapDate",
      headerName: "Target Date",
      width: 120,
      valueFormatter: (params) => params.value ? displayDate(params.value) : "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 120,
      valueFormatter: (params) => params.value ? displayDate(params.value) : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        if (params.row.status !== "pending") return null;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {canRespondSwap && (
              <>
                <Tooltip title="Accept">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleAction("accept", params.row)}
                    disabled={actionLoading}
                  >
                    <AcceptIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleAction("reject", params.row)}
                    disabled={actionLoading}
                  >
                    <RejectIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                color="default"
                onClick={() => handleAction("cancel", params.row)}
                disabled={actionLoading}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Extend request columns
  const extendColumns = [
    {
      field: "extendingTeacher",
      headerName: "Extending Teacher",
      width: 160,
      valueGetter: (params) =>
        params.row.extendingTeacher?.name || params.row.extendingTeacherName || "—",
    },
    {
      field: "affectedTeacher",
      headerName: "Affected Teacher",
      width: 160,
      valueGetter: (params) =>
        params.row.affectedTeacher?.name || params.row.affectedTeacherName || "—",
    },
    {
      field: "sourceSlot",
      headerName: "Source Slot",
      width: 180,
      valueGetter: (params) => {
        const slot = params.row.sourceSlot || {};
        return `${slot.dayOfWeek || ""} ${slot.startTime || ""}-${slot.endTime || ""}`;
      },
    },
    {
      field: "targetSlot",
      headerName: "Target Slot (Consumed)",
      width: 180,
      valueGetter: (params) => {
        const slot = params.row.targetSlot || {};
        return `${slot.dayOfWeek || ""} ${slot.startTime || ""}-${slot.endTime || ""}`;
      },
    },
    {
      field: "extendDate",
      headerName: "Extend Date",
      width: 120,
      valueFormatter: (params) => params.value ? displayDate(params.value) : "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 120,
      valueFormatter: (params) => params.value ? displayDate(params.value) : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        if (params.row.status !== "pending") return null;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {canRespondExtend && (
              <>
                <Tooltip title="Accept">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleAction("accept", params.row)}
                    disabled={actionLoading}
                  >
                    <AcceptIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleAction("reject", params.row)}
                    disabled={actionLoading}
                  >
                    <RejectIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                color="default"
                onClick={() => handleAction("cancel", params.row)}
                disabled={actionLoading}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Lecture Swap & Extend
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage swap and extend requests for lecture slots
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {canForceSwap && (
            <Button
              variant="outlined"
              startIcon={<ForceIcon />}
              onClick={handleForceSwap}
              size="small"
            >
              Force Swap
            </Button>
          )}
          {canForceExtend && (
            <Button
              variant="outlined"
              startIcon={<ForceIcon />}
              onClick={handleForceExtend}
              size="small"
            >
              Force Extend
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => activeTab === 0 ? fetchSwapRequests() : fetchExtendRequests()}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      )}
      {(swapError || extendError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setSwapError(""); setExtendError(""); }}>
          {swapError || extendError}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab icon={<SwapIcon />} iconPosition="start" label="Swap Requests" />
          <Tab icon={<ExtendIcon />} iconPosition="start" label="Extend Requests" />
        </Tabs>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 140 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {activeTab === 0 && (
              <TextField
                select
                label="Swap Mode"
                value={swapModeFilter}
                onChange={(e) => setSwapModeFilter(e.target.value)}
                size="small"
                sx={{ minWidth: 140 }}
              >
                {SWAP_MODE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <DatePicker
              label="From Date"
              value={dateFrom}
              onChange={setDateFrom}
              format={DATE_DISPLAY_FORMAT}
              slotProps={{
                textField: { size: "small", sx: { width: 160 } },
                field: { clearable: true },
              }}
            />
            <DatePicker
              label="To Date"
              value={dateTo}
              onChange={setDateTo}
              format={DATE_DISPLAY_FORMAT}
              slotProps={{
                textField: { size: "small", sx: { width: 160 } },
                field: { clearable: true },
              }}
            />

            <Button
              variant="text"
              size="small"
              onClick={() => {
                setStatusFilter("");
                setSwapModeFilter("");
                setDateFrom(null);
                setDateTo(null);
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent>
          {activeTab === 0 ? (
            <DataGrid
              rows={swapRequests}
              columns={swapColumns}
              rowCount={swapTotal}
              loading={swapLoading}
              paginationMode="server"
              page={swapPage}
              pageSize={swapPageSize}
              onPageChange={(newPage) => setSwapPage(newPage)}
              onPageSizeChange={(newSize) => {
                setSwapPageSize(newSize);
                setSwapPage(0);
              }}
              rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              autoHeight
              disableSelectionOnClick
              getRowId={(row) => row.uuid || row.id}
              sx={{
                "& .MuiDataGrid-row": { cursor: "pointer" },
              }}
            />
          ) : (
            <DataGrid
              rows={extendRequests}
              columns={extendColumns}
              rowCount={extendTotal}
              loading={extendLoading}
              paginationMode="server"
              page={extendPage}
              pageSize={extendPageSize}
              onPageChange={(newPage) => setExtendPage(newPage)}
              onPageSizeChange={(newSize) => {
                setExtendPageSize(newSize);
                setExtendPage(0);
              }}
              rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              autoHeight
              disableSelectionOnClick
              getRowId={(row) => row.uuid || row.id}
              sx={{
                "& .MuiDataGrid-row": { cursor: "pointer" },
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Confirm Action Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, request: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Confirm {confirmDialog.action?.charAt(0).toUpperCase()}{confirmDialog.action?.slice(1)}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to <strong>{confirmDialog.action}</strong> this{" "}
            {activeTab === 0 ? "swap" : "extend"} request?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, request: null })}>
            No
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.action === "accept" ? "success" : confirmDialog.action === "reject" ? "error" : "primary"}
            onClick={executeAction}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : `Yes, ${confirmDialog.action}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LectureSwapManagement;
